import React, { useRef, useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { usePDFEditor } from './usePDFEditor';
import { FriendlyColumn, PDFEditorProps } from './types';
import { useSnackbar } from '../../../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../../../context/Snackbar/Types';
import { PDFToolbar } from './components/PDFToolbar/PDFToolbar';
import { PDFCanvas } from './components/PDFCanvas/PDFCanvas';
import { PDFPropertiesPanel } from './components/PDFPropertiesPanel/PDFPropertiesPanel';
import { usePDFPageImage } from './usePDFPageImage';
import { useCoordinateConversion } from './useCoordinateConversion';
import { useDragPlaceholder } from './useDragPlaceholder';

export const PDFEditor: React.FC<PDFEditorProps> = ({ templateId }) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [staticTextInput, setStaticTextInput] = useState('');
  const [customVariableInput, setCustomVariableInput] = useState('');
  const { showSnackbar } = useSnackbar();

  const {
    pageCount,
    pageSizes,
    currentPage,
    placeholders,
    selectedPlaceholderId,
    scale,
    loading,
    error,
    dbColumns,
    friendlyColumns,
    saving,
    popupState,
    setCurrentPage,
    updatePlaceholder,
    deletePlaceholder,
    selectPlaceholder,
    setScale,
    savePlaceholders,
    openPopup,
    closePopup,
    setPopupMode,
    selectColumnInPopup,
    createStaticTextInPopup,
    createCustomVariableInPopup,
    getCurrentPagePlaceholders,
    getSelectedPlaceholder,
    getGroupedFriendlyColumns,
  } = usePDFEditor({ templateId });

  const currentPageSize = pageSizes[currentPage - 1];
  if (!loading && pageCount > 0 && !currentPageSize) {
    return (
      <Alert severity="error">
        MISSING PAGE SIZE for page {currentPage} — pageSizes has {pageSizes.length} entries. Fix data loading.
      </Alert>
    );
  }

  const safePageSize = currentPageSize ?? { width: 0, height: 0 };
  const displayWidth = safePageSize.width * scale;
  const displayHeight = safePageSize.height * scale;
  const currentPagePlaceholders = getCurrentPagePlaceholders();
  const selectedPlaceholder = getSelectedPlaceholder();
  const groupedColumns = getGroupedFriendlyColumns();

  const { pageImageUrl, imageLoading } = usePDFPageImage({
    templateId,
    currentPage,
    scale,
    ready: !loading && pageCount > 0,
  });

  const { screenToPDF, pdfToScreen } = useCoordinateConversion({
    scale,
    pageHeight: safePageSize.height,
    canvasRef,
  });

  const drag = useDragPlaceholder({
    placeholders,
    scale,
    updatePlaceholder,
    selectPlaceholder,
  });

  // Single click handler for adding new placeholder
  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (drag.isDragging) return;

    const { x: pdfX, y: pdfY } = screenToPDF(e.clientX, e.clientY);
    const clickedPlaceholder = currentPagePlaceholders.find(p => {
      return pdfX >= p.rect.x && pdfX <= p.rect.x + p.rect.w &&
             pdfY >= p.rect.y && pdfY <= p.rect.y + p.rect.h;
    });

    if (clickedPlaceholder) {
      selectPlaceholder(clickedPlaceholder.id);
      return;
    }

    openPopup(e.clientX, e.clientY, pdfX, pdfY);
  };

  const handleSave = async () => {
    const success = await savePlaceholders();
    if (success) {
      setSaveSuccess(true);
      showSnackbar({ title: 'Success', message: 'Template saved successfully!', type: SnackbarType.success });
      setTimeout(() => setSaveSuccess(false), 3000);
    } else {
      showSnackbar({ title: 'Error', message: 'Failed to save placeholders', type: SnackbarType.error });
    }
  };

  const handleZoomIn = () => setScale(Math.min(scale + 0.25, 3));
  const handleZoomOut = () => setScale(Math.max(scale - 0.25, 0.5));

  // Filter columns based on search
  const filteredGroupedColumns = useMemo(() => {
    if (!columnSearch) return groupedColumns;
    const result: Record<string, FriendlyColumn[]> = {};
    Object.entries(groupedColumns).forEach(([group, cols]) => {
      const filtered = cols.filter(c =>
        c.display.toLowerCase().includes(columnSearch.toLowerCase())
      );
      if (filtered.length > 0) {
        result[group] = filtered;
      }
    });
    return result;
  }, [groupedColumns, columnSearch]);

  // Get display name for a column key
  const getColumnDisplayName = (key: string | null) => {
    if (!key) return '';
    const col = friendlyColumns.find(c => c.key === key);
    return col?.display || key;
  };

  // Calculate placeholder screen position with drag offset
  const getPlaceholderScreenPos = (p: typeof placeholders[0]) => {
    return drag.getPlaceholderScreenPos(p, pdfToScreen);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const handlePopupModeChange = (newMode: 'variable' | 'static_text' | 'custom_variable') => {
    setPopupMode(newMode);
    setStaticTextInput('');
    setCustomVariableInput('');
    setColumnSearch('');
  };

  const handleCreateStaticText = (text: string) => {
    createStaticTextInPopup(text);
    setStaticTextInput('');
  };

  const handleCreateCustomVariable = (name: string) => {
    createCustomVariableInPopup(name);
    setCustomVariableInput('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <PDFToolbar
        scale={scale}
        pageCount={pageCount}
        currentPage={currentPage}
        saving={saving}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onPageChange={setCurrentPage}
        onSave={handleSave}
      />

      {saveSuccess && (
        <Alert severity="success">Placeholders saved successfully!</Alert>
      )}

      <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden' }}>
        <PDFCanvas
          canvasRef={canvasRef}
          displayWidth={displayWidth}
          displayHeight={displayHeight}
          isDragging={drag.isDragging}
          currentPage={currentPage}
          pageImageUrl={pageImageUrl}
          imageLoading={imageLoading}
          currentPagePlaceholders={currentPagePlaceholders}
          selectedPlaceholderId={selectedPlaceholderId}
          dragPlaceholderId={drag.dragPlaceholderId}
          popupState={popupState}
          currentPageSize={safePageSize}
          scale={scale}
          onCanvasClick={handleCanvasClick}
          onPlaceholderMouseDown={drag.handlePlaceholderMouseDown}
          getPlaceholderScreenPos={getPlaceholderScreenPos}
          getColumnDisplayName={getColumnDisplayName}
          selectPlaceholder={selectPlaceholder}
        />

        <PDFPropertiesPanel
          popupState={popupState}
          currentPage={currentPage}
          currentPagePlaceholders={currentPagePlaceholders}
          selectedPlaceholder={selectedPlaceholder}
          selectedPlaceholderId={selectedPlaceholderId}
          dbColumns={dbColumns}
          filteredGroupedColumns={filteredGroupedColumns}
          columnSearch={columnSearch}
          staticTextInput={staticTextInput}
          customVariableInput={customVariableInput}
          onCustomVariableInputChange={setCustomVariableInput}
          onCreateCustomVariable={handleCreateCustomVariable}
          onPopupModeChange={handlePopupModeChange}
          onClosePopup={closePopup}
          onColumnSearchChange={setColumnSearch}
          onSelectColumnInPopup={selectColumnInPopup}
          onStaticTextInputChange={setStaticTextInput}
          onCreateStaticText={handleCreateStaticText}
          onSelectPlaceholder={selectPlaceholder}
          onUpdatePlaceholder={updatePlaceholder}
          onDeletePlaceholder={deletePlaceholder}
          getColumnDisplayName={getColumnDisplayName}
        />
      </Box>
    </Box>
  );
};
